import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { AuthProvider } from './context/AuthContext'
import { ThemeProvider } from './components/ThemeProvider'
import { ConfirmProvider } from './components/ConfirmDialog'
import { I18nProvider } from './i18n/I18nProvider'
import { ApiError } from './lib/api'
import { registerServiceWorker } from './lib/serviceWorker'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,

      /*
       * Refetch when the window regains focus.
       *
       * This does more for "live" than any polling interval. Nobody watches a
       * dashboard continuously — they switch to it from something else, and
       * this is what makes the data correct on the frame they look at it.
       *
       * It was off because it fires on every alt-tab; `staleTime` is what makes
       * that affordable, since a query fetched within the last 30 seconds is
       * served from cache and costs nothing.
       */
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Retrying a 401/403/404/422 just wastes a round trip and delays the
        // error the user needs to see. Network failures are worth retrying,
        // because on the plant wifi they are usually transient.
        if (error instanceof ApiError && !error.isOffline) return false
        return failureCount < 2
      },
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {/*
          Inside QueryClientProvider, because switching language has to clear
          the cache: half the strings on screen are labels the server sent, and
          a cached response is in whatever language it was fetched in.

          Outside AuthProvider, because the sign-in screen needs a language
          before anybody has an account — and because AuthProvider must be free
          to use `t()` without a circular dependency between the two.
        */}
        <I18nProvider>
          {/*
            Inside I18nProvider, because the dialog's own buttons are
            translated — and that is half the reason it exists: `window.confirm`
            renders "OK" and "Cancel" in the *browser's* language, so a Bosnian
            question got English buttons on an English phone.
          */}
          <ConfirmProvider>
            <BrowserRouter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </BrowserRouter>
          </ConfirmProvider>
        </I18nProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
)

/*
 * Registered here rather than by vite-plugin-pwa, purely so it can be given
 * `updateViaCache: 'none'`. See lib/serviceWorker.ts — without it a cache
 * header on sw.js can freeze the app on one build, permanently, on that device.
 */
registerServiceWorker()
